/**
 * Blueprint Code Preview using Export Templates
 * Generates code preview for blueprints using the custom export template system
 */

import { ExportTemplate, ExportTemplatesConfig, TemplateRenderContext } from '@/types/exportTemplates';
import { getPresetTemplates } from './exportTemplatePresets';
import { renderExportTemplate, mapBlueprintField } from './templateEngine';
import { Game } from '@/lib/stores/storeTypes';
import { getDefaultPlatformForGame } from '@/lib/utils/platformMapping';
import { getCachedTemplates } from "@/lib/stores/templatesStore";
import { sanitizeFilename } from './libraryProcessorShared';

/**
 * Get the active export template for a game
 */
function getActiveTemplate(game: Game | null, platform?: string): ExportTemplate | null {
  if (!game) return null;
  
  const exportConfig = (game.settings as any)?.exportTemplates as ExportTemplatesConfig | undefined;
  
  // If specific platform requested, prioritize based on active template
  if (platform) {
    // For Roblox, prioritize Database templates over code templates
    if (platform === 'roblox' && exportConfig?.templates) {
      // First, check for database templates (these take priority for Roblox)
      const databaseTemplate = exportConfig.templates.find(t => 
        (t.id === 'preset-roblox-database' || t.id === 'preset-roblox-static-typed') && 
        t.platform === 'roblox'
      );
      if (databaseTemplate) {
        console.log('🎯 Using Roblox Database template for blueprint export:', databaseTemplate.name);
        return databaseTemplate;
      }
    }
    
    // First, check if the active template matches the requested platform
    if (exportConfig?.activeTemplateId && exportConfig.templates) {
      const activeTemplate = exportConfig.templates.find(t => t.id === exportConfig.activeTemplateId);
      if (activeTemplate && activeTemplate.platform === platform) {
        return activeTemplate;
      }
    }
    
    // If active template doesn't match, find any template for that platform
    if (exportConfig?.templates) {
      const template = exportConfig.templates.find(t => t.platform === platform);
      if (template) return template;
    }
    
    // Fallback to preset for that platform
    const presets = getPresetTemplates();
    const preset = presets.find(p => p.platform === platform);
    if (preset) return preset;
  }
  
  // Use active template (when no specific platform requested)
  if (exportConfig?.activeTemplateId && exportConfig.templates) {
    const template = exportConfig.templates.find(t => t.id === exportConfig.activeTemplateId);
    if (template) return template;
  }
  
  // Fallback to default platform preset
  const defaultPlatform = getDefaultPlatformForGame(game);
  const presets = getPresetTemplates();
  return presets.find(p => p.platform === defaultPlatform) || presets[0];
}

/**
 * Generate blueprint code preview with game context
 */
export function generateBlueprintCodePreviewWithGame(
  blueprint: any,
  game: Game | null,
  platform?: 'roblox' | 'unity' | 'godot' | 'cpp' | 'typescript'
): string | null {
  if (!blueprint) return null;
  
  // Get active template
  const template = getActiveTemplate(game, platform);
  if (!template) return null;
  
  // Extract fields from blueprint data
  const fields: any[] = [];
  if (blueprint.data && typeof blueprint.data === 'object') {
    const data = blueprint.data as any;
    
    if (data.sections) {
      Object.values(data.sections).forEach((section: any) => {
        if (section && typeof section === 'object') {
          // Skip section metadata fields
          const sectionFields = Object.entries(section).filter(
            ([key]) => !key.startsWith('_')
          );
          
          sectionFields.forEach(([key, field]: [string, any]) => {
            if (field && typeof field === 'object' && field.id && field.label) {
              fields.push(mapBlueprintField(field, undefined, template));
            }
          });
        }
      });
    }
  }
  
  // Create render context
  const context: TemplateRenderContext = {
    templateName: blueprint.name,
    templateDescription: blueprint.description,
    fields: fields,
    metadata: {
      blueprintId: blueprint.id,
      version: blueprint.version
    }
  };
  
  // Render template
  const result = renderExportTemplate(context, template);
  
  // Return type file content (the blueprint definition)
  return result.typeFile?.content || null;
}

/**
 * Download blueprint code file
 */
export function downloadBlueprintCode(
  blueprint: any,
  game: Game | null,
  platform: 'roblox' | 'unity' | 'godot' | 'cpp' | 'typescript',
  gameName?: string,
  templateName?: string
): void {
  const code = generateBlueprintCodePreviewWithGame(blueprint, game, platform);
  
  if (!code) {
    console.error('Failed to generate code for download');
    return;
  }
  
  // Get template for file extension
  const template = getActiveTemplate(game, platform);
  const extension = template?.typeFile.filename.split('.').pop() || 'txt';
  
  // Generate filename
  const name = templateName || blueprint.name || 'template';
  const filename = `${sanitizeFilename(gameName || 'game')}-T-${sanitizeFilename(name)}.${extension}`;
  
  // Create and download file
  const blob = new Blob([code], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
